import { createUseStyles } from "react-jss";
import { Chip, Tooltip, Box } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { theme } from "$utils/theme/bataillog";
import Text from "$components/Text";
import { ProductQuantity } from "$types";
import { ReceptionReference } from "$types/ReceptionReference";
import ReferenceType from "$types/ReferenceType";
import JTooltip from "../JTooltip/JTooltip";
import { QTY_WIDTH, UNIT_WIDTH } from "./JReceptionTree";
import { ProductService, ReceptionService } from "$services";
import useReception from "$hooks/useReception";
import { Popin } from "$components/Popin/Popin";
import ReferencingForm from "$containers/Receptions/Referencing/ReferencingForm";
import BlueTextButton from "$components/JReceptionTree/BlueTextButton";

type Props = {
  disabledMessage?: string;
  productQuantity: ProductQuantity;
  receptionReference: ReceptionReference;
  reception?: any;
  onEdit?: () => Promise<void> | void;
};

// Helper function to normalize orders data structure
const normalizeOrders = (orders: any): any[] => {
  if (!orders) return [];

  // New Strapi format: orders: { data: [...] }
  if (orders.data && Array.isArray(orders.data)) {
    return orders.data.map((order: any) => ({
      id: order.id,
      ...order.attributes,
    }));
  }

  // Old format: orders: [...]
  if (Array.isArray(orders)) {
    return orders;
  }

  // Fallback
  return [];
};

function ProductQuantityLabel({
  disabledMessage,
  productQuantity,
  receptionReference,
  reception,
  onEdit,
}: Props) {
  const classes = useStyles();
  const {
    referencingOpen,
    toggleReferencingPopin,
    focusedReceptionReferenceId,
  } = useReception();
  const {
    orders: rawOrders,
    originalProductReference,
    productReference,
    referenceType,
    id: receptionReferenceId,
  } = receptionReference;
  const [shouldRefreshAfterClose, setShouldRefreshAfterClose] = useState(false);
  const isCurrentPopin = focusedReceptionReferenceId === receptionReferenceId;

  const { PRODUCT } = ReferenceType;
  const { format: formatNb } = new Intl.NumberFormat("fr-FR");
  const { receivedQuantity, productId, suborderReference, orderReference } =
    productQuantity;
  const [fullProduct, setFullProduct] = useState(null);

  // Normalize orders data structure
  const orders = normalizeOrders(rawOrders);

  const fetchProductById = useCallback(async () => {
    try {
      const p = await ProductService.getProductByReference({
        reference: productId,
      });
      setFullProduct(p.length ? p[0] : null);
    } catch (error) {
      console.error("Error fetching product:", error);
      setFullProduct(null);
    }
  }, [productId]);

  useEffect(() => {
    fetchProductById();
  }, [fetchProductById]);

  const reference =
    referenceType === PRODUCT ? productReference : fullProduct?.reference;

  // Now orders is properly normalized array, so .find() will work
  const targetOrder = orders.find((o) => o.reference === orderReference);
  const hasOnlyOneSuborder = targetOrder?.suborders?.length === 1;

  const handleOnSubmit = useCallback(async (data: any) => {
    await ReceptionService.updateReceptionItem(data.id, data);
    setShouldRefreshAfterClose(true);
  }, []);

  useEffect(() => {
    if (!referencingOpen && shouldRefreshAfterClose) {
      setShouldRefreshAfterClose(false);
      if (onEdit) {
        onEdit();
      }
    }
  }, [referencingOpen, shouldRefreshAfterClose, onEdit]);

  // Show the referencing link only for product references
  const showProductReferencingLink = referenceType === PRODUCT && reception;

  return (
    <>
      <JTooltip title={disabledMessage}>
        <div className={classes.wrapper}>
          <div className={classes.productInfo}>
            <div className={classes.references}>
              <Text type="secondary" className={classes.suborderRef}>
                {hasOnlyOneSuborder ? orderReference : suborderReference}
              </Text>
              <Tooltip title={reference}>
                <b className={classes.reference}>{reference}</b>
              </Tooltip>
              {referenceType === PRODUCT && (
                <Tooltip title={originalProductReference}>
                  <span>
                    <Text className={classes.reference}>
                      {originalProductReference}
                    </Text>
                  </span>
                </Tooltip>
              )}
            </div>
            {fullProduct?.designation && (
              <Text className={classes.designation}>
                {fullProduct.designation}
              </Text>
            )}
          </div>
          <div className={classes.rightSection}>
            {showProductReferencingLink && (
              <Box textAlign="right">
                <BlueTextButton
                  onClick={() =>
                    toggleReferencingPopin(true, receptionReferenceId)
                  }
                >
                  Ref. à l&apos;article
                </BlueTextButton>
              </Box>
            )}
            {fullProduct && (
              <div className={classes.qtyAndUnit}>
                <Chip
                  size="small"
                  label={formatNb(receivedQuantity)}
                  className={classes.qty}
                />
                <Text type="secondary" className={classes.unit}>
                  ({fullProduct.unit?.toUpperCase()})
                </Text>
              </div>
            )}
          </div>
        </div>
      </JTooltip>

      {/* Popin for product referencing */}
      {showProductReferencingLink && (
        <Popin
          openPopin={referencingOpen}
          toggleDrawer={toggleReferencingPopin}
        >
          {isCurrentPopin && (
            <>
              <Text type="h2">Référencement</Text>
              <ReferencingForm
                focusedReceptionReferenceId={focusedReceptionReferenceId}
                isReadOnly={false}
                isSavingReception={false}
                onSubmit={handleOnSubmit}
                reception={reception}
                togglePopin={toggleReferencingPopin}
              />
            </>
          )}
        </Popin>
      )}
    </>
  );
}

export default ProductQuantityLabel;

const useStyles = createUseStyles(() => ({
  ellipsedText: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  suborderRef: {
    composes: "$ellipsedText",
    display: "inline-block",
    margin: `0 ${theme.spacing(2)}px 0 0`,
  },
  designation: {
    composes: "$ellipsedText",
    flex: "0 1 auto",
  },
  references: {
    alignItems: "center",
    display: "flex",
    flex: 1,
  },
  reference: {
    composes: "$ellipsedText",
    fontSize: "90%",
    margin: `0 ${theme.spacing(2)}px 0 0`,
  },
  productInfo: {
    alignItems: "center",
    display: "flex",
    overflow: "hidden",
    flex: 1,
  },
  wrapper: {
    alignItems: "center",
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(1),
  },
  rightSection: {
    alignItems: "center",
    display: "flex",
    flexShrink: 0,
    gap: theme.spacing(1),
  },
  qtyAndUnit: {
    alignItems: "center",
    display: "flex",
    flexShrink: 0,
    width: QTY_WIDTH + UNIT_WIDTH,
  },
  qty: {
    margin: `0 ${theme.spacing(1)}px 0 0`,
    width: QTY_WIDTH,
  },
  unit: {
    composes: "$ellipsedText",
    margin: 0,
    flex: 1,
    width: UNIT_WIDTH,
  },
}));
